import React, { useState } from "react";

/**
 * SchedulingSettings
 *
 * Manage block scheduling, session times, conflict minimization, and breaks.
 * - Toggle block scheduling on/off
 * - Set session durations, break intervals
 * - Enable conflict minimization
 * - Lock or reorder performance automatically
 */
const SchedulingSettings = () => {
  // Example toggles/inputs
  const [blockSchedulingEnabled, setBlockSchedulingEnabled] = useState(true);
  const [sessionLengthMinutes, setSessionLengthMinutes] = useState(120); // default 2 hours
  const [breakLengthMinutes, setBreakLengthMinutes] = useState(15);
  const [minimizeConflicts, setMinimizeConflicts] = useState(false);
  const [autoReorderRoutines, setAutoReorderRoutines] = useState(true);
  const [lockScheduleAfterFinal, setLockScheduleAfterFinal] = useState(false);

  // Example function to handle saving
  const handleSave = () => {
    console.log("Scheduling settings saved:", {
      blockSchedulingEnabled,
      sessionLengthMinutes,
      breakLengthMinutes,
      minimizeConflicts,
      autoReorderRoutines,
      lockScheduleAfterFinal,
    });
    alert("Scheduling settings have been saved (simulated)!");
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Page Title */}
      <h1 className="text-2xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
        Scheduling & Performance Order
      </h1>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
        Manage block scheduling, session times, conflict minimization, and breaks.
      </p>

      {/* Block Scheduling */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm space-y-4 transition-colors">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Block Scheduling
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Group routines into blocks or sessions to simplify scheduling.
        </p>

        <div className="space-y-3">
          <div className="flex items-center">
            <input
              id="blockSchedulingEnabled"
              type="checkbox"
              className="mr-2 w-4 h-4 accent-blue-600"
              checked={blockSchedulingEnabled}
              onChange={(e) => setBlockSchedulingEnabled(e.target.checked)}
            />
            <label
              htmlFor="blockSchedulingEnabled"
              className="text-gray-700 dark:text-gray-300"
            >
              Enable block-based scheduling
            </label>
          </div>

          {/* Session and Break Length */}
          {blockSchedulingEnabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div className="flex flex-col">
                <label
                  htmlFor="sessionLengthMinutes"
                  className="font-medium text-gray-700 dark:text-gray-300"
                >
                  Session Length (minutes)
                </label>
                <input
                  id="sessionLengthMinutes"
                  type="number"
                  min={15}
                  className="mt-1 p-2 border border-gray-300 dark:border-gray-600 rounded 
                             focus:outline-none focus:ring-2 focus:ring-blue-500
                             dark:bg-gray-700 dark:text-gray-100 w-24"
                  value={sessionLengthMinutes}
                  onChange={(e) => setSessionLengthMinutes(Number(e.target.value))}
                />
                <small className="text-gray-500 dark:text-gray-400 mt-1">
                  How long each block or session should last
                </small>
              </div>
              <div className="flex flex-col">
                <label
                  htmlFor="breakLengthMinutes"
                  className="font-medium text-gray-700 dark:text-gray-300"
                >
                  Break Length (minutes)
                </label>
                <input
                  id="breakLengthMinutes"
                  type="number"
                  min={5}
                  className="mt-1 p-2 border border-gray-300 dark:border-gray-600 rounded 
                             focus:outline-none focus:ring-2 focus:ring-blue-500
                             dark:bg-gray-700 dark:text-gray-100 w-24"
                  value={breakLengthMinutes}
                  onChange={(e) => setBreakLengthMinutes(Number(e.target.value))}
                />
                <small className="text-gray-500 dark:text-gray-400 mt-1">
                  Short break time between sessions
                </small>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Conflict Minimization */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm space-y-4 transition-colors">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Conflict Minimization
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Automatically reorder routines to reduce back-to-back costume conflicts, etc.
        </p>
        <div className="flex items-center">
          <input
            id="minimizeConflicts"
            type="checkbox"
            className="mr-2 w-4 h-4 accent-blue-600"
            checked={minimizeConflicts}
            onChange={(e) => setMinimizeConflicts(e.target.checked)}
          />
          <label htmlFor="minimizeConflicts" className="text-gray-700 dark:text-gray-300">
            Enable conflict minimization
          </label>
        </div>
      </section>

      {/* Performance Order */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm space-y-4 transition-colors">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Performance Order
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Control how routines are ordered and if the schedule is locked once finalized.
        </p>
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              id="autoReorderRoutines"
              type="checkbox"
              className="mr-2 w-4 h-4 accent-blue-600"
              checked={autoReorderRoutines}
              onChange={(e) => setAutoReorderRoutines(e.target.checked)}
            />
            <label
              htmlFor="autoReorderRoutines"
              className="text-gray-700 dark:text-gray-300"
            >
              Automatically reorder routines for best flow
            </label>
          </div>

          <div className="flex items-center">
            <input
              id="lockScheduleAfterFinal"
              type="checkbox"
              className="mr-2 w-4 h-4 accent-blue-600"
              checked={lockScheduleAfterFinal}
              onChange={(e) => setLockScheduleAfterFinal(e.target.checked)}
            />
            <label
              htmlFor="lockScheduleAfterFinal"
              className="text-gray-700 dark:text-gray-300"
            >
              Lock the schedule after finalizing to prevent accidental changes
            </label>
          </div>
        </div>
      </section>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white 
                     font-semibold rounded shadow transition-colors"
        >
          Save Scheduling
        </button>
      </div>
    </div>
  );
};

export default SchedulingSettings;
